import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Lock,
  Business as BusinessIcon,
  Logout,
  Person,
  FolderOpen,
  DarkMode,
  LightMode,
  Chat,
  Newspaper,
  School,
  HelpOutline,
  Home as HomeIcon,
  Search,
  Security,
  CreditCard,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useLoginDialog } from './contexts/LoginDialogContext';
import { useTheme as useAppTheme } from './contexts/ThemeContext';
import UserAvatarMUI from './components/UserAvatarMUI';
import GlobalStockSearch from './components/GlobalStockSearch';
import analyticsService, { ANALYTICS_SOURCES } from './services/analytics';
import { getVersionString, getBuildDate } from './version';
import NotificationBell from './components/levelup/NotificationBell';
import CurrencySelector from './components/CurrencySelector';
import LevelProgressRing from './components/levelup/LevelProgressRing';
import { useLevelUpStore, getLevelProgressPercent } from './stores/levelUpStore';
import { useHistoryMigration } from './hooks/useHistoryMigration';
import ActiveWorkIndicator from './components/common/ActiveWorkIndicator';
import RailWorkDot from './components/common/RailWorkDot';

interface Tool {
  id: string;
  name: string;
  description: string;
  icon?: React.ReactNode;
  available: boolean;
  route: string;
  requiresAuth?: boolean;
}

// Professional Tools (Mercury redesign)
const tools: Tool[] = [
  {
    id: 'portfolio-analysis',
    // The work product, not the tool ([[ROADMAP A.26]], owner 2026-08-30) —
    // "Risk Analysis Reports" is the noun a subscriber thinks in, and the
    // destination now opens on the reports rather than on a list of books.
    name: 'Risk Analysis Reports',
    description: 'Your analyses and rebalance comparisons',
    icon: <Security />,
    available: true,
    route: '/portfolio/risk-assessment',
    requiresAuth: false
  },
  {
    id: 'stock-research',
    name: 'Stock Research',
    description: 'Fundamentals and sentiment',
    icon: <Search />,
    available: true,
    route: '/research',
    requiresAuth: false
  },
  {
    id: 'ai-chat',
    name: 'AI Assistant',
    description: 'Ask questions and get insights',
    icon: <Chat />,
    available: true,
    route: '/chat',
    requiresAuth: false
  }
];

// Pricing plans are now imported from ./data/pricingData

// Navigation Rail constants (desktop only)
const NAV_RAIL_WIDTH = 56;

// Navigation items for the desktop rail. Exported: the phone menu is derived
// from this list, in this order, so the two cannot drift ([[BUGS #181]]).
export const navRailItems = [
  { icon: <HomeIcon />, label: 'Home', path: '/' },
  { icon: <Security />, label: 'Risk Analysis Reports', path: '/portfolio/risk-assessment' },
  { icon: <Search />, label: 'Stock Research', path: '/research' },
  { icon: <Chat />, label: 'AI Assistant', path: '/chat' },
  // `Newspaper`, not `Article`: at 24px in a monochrome rail, MUI's `Article`
  // (a page with ruled lines) and `Chat` (a bubble with ruled lines) are both
  // rounded rectangles with horizontal strokes, and the two sat adjacent —
  // indistinguishable at a glance (owner, 2026-08-27). A folded newspaper has a
  // different silhouette, and it avoids Academy's book as well.
  { icon: <Newspaper />, label: 'Blog', path: '/blog' },
  { icon: <School />, label: 'Academy', path: '/academy' },
  { icon: <HelpOutline />, label: 'FAQ', path: '/faq' },
];

/**
 * What the MOBILE drawer shows under "Resources" — derived, never hand-written.
 *
 * The rail's items minus Home (which has its own button at the top of the
 * drawer) and minus anything already listed under "Professional Tools". Written
 * as a derivation because the hand-written version is what let Blog reach the
 * desktop rail and never the phone ([[BUGS #181]]).
 */
export const drawerResourceItems = navRailItems.filter(
  (item) => item.path !== '/' && !tools.some((t) => t.route === item.path),
);

const Layout: React.FC = () => {
  const theme = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useTheme();
  const { effectiveTheme, toggleTheme } = useAppTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  // Desktop detection for navigation rail (≥900px)
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'));
  const { user, signOut } = useAuth();
  const { openLoginDialog } = useLoginDialog();

  // Migrate session history to Firestore when user logs in
  useHistoryMigration();
  // Granular selectors to prevent re-renders when unrelated store state changes.
  // Selecting primitive values (number) instead of objects avoids new-reference re-renders.
  const currentLevel = useLevelUpStore((s) => s.levelProgress.currentLevel);
  const starsEarned = useLevelUpStore((s) => s.starsEarned);
  const quizResults = useLevelUpStore((s) => s.levelProgress.quizResults);
  const levelProgressPercent = getLevelProgressPercent(currentLevel, starsEarned, quizResults);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      analyticsService.trackSignOut(ANALYTICS_SOURCES.NAVBAR);
      await signOut();
      handleUserMenuClose();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const drawer = (
    <Box sx={{ width: 280, height: '100%', bgcolor: muiTheme.palette.mode === 'dark' ? '#121212' : 'background.paper' }}>
      <Box sx={{
        p: 3,
        bgcolor: muiTheme.palette.mode === 'dark' ? '#1e1e1e' : muiTheme.palette.grey[100],
      }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          {/* The IA mark. The onError→jpg fallback is gone with the webp it was
              catching for; a single PNG has nothing to fall back to. */}
          <Avatar
            src="/logo192.png"
            alt=""
            sx={{ width: 50, height: 50, bgcolor: 'white' }}
          />
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              color: muiTheme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.95)'
                : muiTheme.palette.grey[900]
            }}
          >
            IndexAlpha
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: muiTheme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.7)'
              : muiTheme.palette.grey[600]
          }}
        >
          Professional Financial Intelligence
        </Typography>
      </Box>
      
      <List sx={{ px: 2, py: 3 }}>
        {/* Home button */}
        <ListItem disablePadding sx={{ mb: 2 }}>
          <ListItemButton
            onClick={() => {
              navigate('/');
              setDrawerOpen(false);
            }}
            selected={location.pathname === '/'}
            sx={{
              borderRadius: 0,
              '&.Mui-selected': {
                bgcolor: 'action.selected',
                border: '2px solid',
                borderColor: 'grey.300',
                color: 'text.primary',
                fontWeight: 600,
                '& .MuiListItemIcon-root': {
                  color: 'text.primary',
                },
              },
            }}
          >
            <ListItemIcon>
              <BusinessIcon />
            </ListItemIcon>
            <ListItemText primary="Home" />
          </ListItemButton>
        </ListItem>
        
        <Divider sx={{ mx: 1, mb: 2 }} />

        <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>
          Professional Tools
        </Typography>
        {tools.map((tool) => (
          <ListItem key={tool.id} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              onClick={() => {
                navigate(tool.route);
                setDrawerOpen(false);
              }}
              selected={location.pathname === tool.route}
              sx={{
                borderRadius: 0,
                '&.Mui-selected': {
                  bgcolor: 'action.selected',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  color: 'text.primary',
                  fontWeight: 600,
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                },
                '&:not(.Mui-selected)': {
                  border: '2px solid transparent',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 48 }}>
                {tool.icon}
              </ListItemIcon>
              <ListItemText
                primary={tool.name}
                secondary={tool.description}
                secondaryTypographyProps={{
                  sx: { fontSize: '0.75rem', mt: 0.5 }
                }}
              />
              {tool.requiresAuth && !user && (
                <Lock sx={{ fontSize: 18, color: 'text.secondary', ml: 1 }} />
              )}
            </ListItemButton>
          </ListItem>
        ))}

        {/* Legacy Agent Routes (Hidden by default, uncomment to show) */}
        {/* <Divider sx={{ mx: 1, my: 2 }} />
        <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>
          Legacy (Deprecated)
        </Typography>
        {reporters.map((reporter) => (
          <ListItem key={reporter.id} disablePadding sx={{ mb: 1 }}>
            <ListItemButton onClick={() => navigate(reporter.route)} selected={location.pathname === reporter.route}>
              <ListItemIcon sx={{ minWidth: 48 }}>{reporter.icon}</ListItemIcon>
              <ListItemText primary={reporter.name} secondary={reporter.description} />
            </ListItemButton>
          </ListItem>
        ))} */}
      </List>
      
      <Divider sx={{ mx: 3 }} />
      
      {user && (
        <>
          <List sx={{ px: 2, py: 2 }}>
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => {
                  analyticsService.trackNavigation('My Portfolios', 'sidebar');
                  navigate('/portfolios');
                  setDrawerOpen(false);
                }}
                selected={location.pathname === '/portfolios'}
                sx={{ borderRadius: 0 }}
              >
                <ListItemIcon>
                  <FolderOpen />
                </ListItemIcon>
                <ListItemText primary="My Portfolios" />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider sx={{ mx: 3 }} />
        </>
      )}
      
      {/* Derived from `navRailItems`, not hand-written ([[BUGS #181]]).
          Academy and FAQ were duplicated here as literal entries while the
          desktop rail kept its own list — so when Blog was added to the rail it
          never reached the phone, and a mobile reader could not open the blog at
          all. Measured at 390px: Home, My Portfolios, Academy, FAQ, My Profile,
          and no Blog.

          `drawerResourceItems` is whatever the rail offers minus Home (its own
          button above) and minus the three in "Professional Tools" — so the next
          item added to the rail appears here by itself, and the two surfaces
          cannot disagree again. */}
      <List sx={{ px: 2, py: 2 }}>
        <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>
          Resources
        </Typography>
        {drawerResourceItems.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              onClick={() => {
                navigate(item.path);
                setDrawerOpen(false);
              }}
              selected={location.pathname.startsWith(item.path)}
              sx={{ borderRadius: 0 }}
            >
              <ListItemIcon sx={{ minWidth: 48 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      
      {user && (
        <>
          <Divider sx={{ mx: 3 }} />
          <List sx={{ px: 2, py: 2 }}>
            <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>
              Settings
            </Typography>
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => {
                  analyticsService.trackNavigation('My Profile', 'sidebar');
                  navigate('/profile');
                  setDrawerOpen(false);
                }}
                selected={location.pathname === '/profile'}
                sx={{ borderRadius: 0 }}
              >
                <ListItemIcon>
                  <Person />
                </ListItemIcon>
                <ListItemText primary="My Profile" />
              </ListItemButton>
            </ListItem>
          </List>
        </>
      )}

      <Divider sx={{ mx: 3 }} />
      <List sx={{ px: 2, py: 2 }}>
        <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>
          Appearance
        </Typography>
        <ListItem disablePadding sx={{ mb: 1 }}>
          <ListItemButton
            onClick={async () => {
              const currentTheme = effectiveTheme;
              await toggleTheme();
              analyticsService.trackThemeToggled(currentTheme, currentTheme === 'light' ? 'dark' : 'light');
            }}
            sx={{ borderRadius: 0 }}
          >
            <ListItemIcon>
              {effectiveTheme === 'light' ? <DarkMode /> : <LightMode />}
            </ListItemIcon>
            <ListItemText
              primary={effectiveTheme === 'light' ? 'Dark Mode' : 'Light Mode'}
              secondary={`Switch to ${effectiveTheme === 'light' ? 'dark' : 'light'} theme`}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="fixed" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center">
            {/* Hamburger menu - only show on mobile/tablet (< md breakpoint) */}
            {!isDesktop && (
              <IconButton
                edge="start"
                aria-label="menu"
                onClick={handleDrawerToggle}
                sx={{
                  color: 'grey.700',
                  mr: 2
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            {/* Desktop nav buttons removed - now in NavigationRail */}

            {/* Global Stock Search — navigates to /research/stock/<symbol> (canonical path-param URL) */}
            <GlobalStockSearch />

            {/* Currency selector — right of search bar, left of notification bell */}
            {!isMobile && <CurrencySelector />}

            {!isMobile && (
              <>
                {user ? (
                  <>
                    {/* Hidden for now - subscription badge removed (text-based badges in menu instead) */}

                    {/* Theme Toggle - HIDDEN: Mercury redesign forces light mode */}
                    {/* <Tooltip title={`Switch to ${effectiveTheme === 'light' ? 'dark' : 'light'} mode`}>
                      <IconButton
                        onClick={toggleTheme}
                        disabled={themeLoading}
                        sx={{
                          mr: 1,
                          bgcolor: effectiveTheme === 'light' ? 'grey.800' : 'transparent',
                          color: effectiveTheme === 'light' ? 'white' : '#FFD700',
                          '&:hover': {
                            bgcolor: effectiveTheme === 'light' ? 'grey.900' : 'rgba(255, 215, 0, 0.1)',
                          }
                        }}
                      >
                        {effectiveTheme === 'light' ? <DarkMode /> : <LightMode />}
                      </IconButton>
                    </Tooltip> */}

                    {/* Notification Bell - Only for authenticated users */}
                    {user && <NotificationBell />}

                    <IconButton
                      onClick={handleUserMenuOpen}
                      sx={{ p: 0 }}
                    >
                      {user ? (
                        <LevelProgressRing level={currentLevel} progressPercent={levelProgressPercent}>
                          <UserAvatarMUI size="medium" />
                        </LevelProgressRing>
                      ) : (
                        <UserAvatarMUI size="medium" />
                      )}
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleUserMenuClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <Box sx={{ px: 2, py: 1.5 }}>
                        <Typography variant="body2">{user.email}</Typography>
                      </Box>
                      <Divider />
                      <MenuItem onClick={() => {
                        navigate('/portfolios');
                        setAnchorEl(null);
                      }}>
                        <ListItemIcon>
                          <FolderOpen fontSize="small" />
                        </ListItemIcon>
                        My Portfolios
                      </MenuItem>
                      {/* My Watchlist is hidden from this menu (owner,
                          2026-08-23) while the product focuses on portfolio
                          tracking. The page and its route still exist and still
                          work — this is a narrowing of what we point people at,
                          not a removal, so restoring it is deleting this
                          comment and the block below. */}
                      <Divider />
                      <MenuItem onClick={() => {
                        navigate('/profile');
                        setAnchorEl(null);
                      }}>
                        <ListItemIcon>
                          <Person fontSize="small" />
                        </ListItemIcon>
                        My Profile
                      </MenuItem>
                      <MenuItem onClick={() => {
                        navigate('/plans');
                        setAnchorEl(null);
                      }}>
                        <ListItemIcon>
                          <CreditCard fontSize="small" />
                        </ListItemIcon>
                        Plans & Billing
                      </MenuItem>
                      <Divider />
                      <MenuItem onClick={handleSignOut}>
                        <ListItemIcon>
                          <Logout fontSize="small" />
                        </ListItemIcon>
                        Sign Out
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      onClick={() => openLoginDialog(ANALYTICS_SOURCES.NAVBAR)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: 'divider',
                        color: 'text.primary',
                        '&:hover': {
                          borderColor: 'text.primary',
                          bgcolor: 'transparent',
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Sign In
                    </Button>
                  </>
                )}
              </>
            )}

          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Global Progress Bar - Removed (Dec 2024) - depended on agent stores */}

      {/* Mobile Drawer - only used on mobile/tablet (< md breakpoint) */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop Navigation Rail - only show on desktop (≥ md breakpoint) */}
      {isDesktop && (
        <Box
          component="nav"
          aria-label="Main navigation"
          sx={{
            width: NAV_RAIL_WIDTH,
            position: 'fixed',
            left: 0,
            top: 64, // Below AppBar
            height: 'calc(100vh - 64px)',
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1100,
          }}
        >
          {/* Navigation items */}
          <Box sx={{ flex: 1 }}>
            {navRailItems.map((item) => {
              // Check if current path matches (exact for home, startsWith for others)
              const isActive = item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path);

              return (
                <Tooltip key={item.path} title={item.label} placement="right" arrow>
                  <IconButton
                    onClick={() => {
                      navigate(item.path);
                      analyticsService.trackNavigation(item.label, ANALYTICS_SOURCES.NAVIGATION_RAIL);
                    }}
                    aria-label={item.label}
                    sx={{
                      width: NAV_RAIL_WIDTH,
                      height: 48,
                      borderRadius: 0,
                      color: isActive ? 'primary.main' : 'text.secondary',
                      bgcolor: isActive ? 'action.selected' : 'transparent',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        color: 'text.primary',
                      },
                    }}
                  >
                    {/* Work in flight shows on the icon where its result
                        will live ([[ROADMAP A.45]]) — the glance-sized cue
                        for the card at bottom-left, never a second voice. */}
                    {item.path === '/portfolio/risk-assessment'
                      ? <RailWorkDot>{item.icon}</RailWorkDot>
                      : item.icon}
                  </IconButton>
                </Tooltip>
              );
            })}
          </Box>

          {/* Theme Toggle at bottom */}
          <Box>
            <Tooltip title={effectiveTheme === 'light' ? 'Dark Mode' : 'Light Mode'} placement="right" arrow>
              <IconButton
                onClick={async () => {
                  const currentTheme = effectiveTheme;
                  await toggleTheme();
                  analyticsService.trackThemeToggled(currentTheme, currentTheme === 'light' ? 'dark' : 'light');
                }}
                aria-label={effectiveTheme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                sx={{
                  width: NAV_RAIL_WIDTH,
                  height: 48,
                  borderRadius: 0,
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                  },
                }}
              >
                {effectiveTheme === 'light' ? <DarkMode /> : <LightMode />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Content + footer share one column that always fills the viewport, so
          the footer sits at the bottom on a short page instead of floating
          mid-screen ([[BUGS #173]], reported against an empty `/blog`).

          The old rule was `minHeight: calc(100vh - 64px - 200px)` on the content
          alone — 200px being a GUESS at the footer's height. The real footer is
          shorter than that, so on a short page the two together stopped above
          the fold and left a strip of bare document canvas below them. Measured
          on staging before this change: `#root` was 618px in a 771px viewport,
          i.e. **153px of exposed canvas**. Letting flexbox measure the footer
          removes the guess rather than correcting it. */}
      <Box sx={{
        mt: 8,
        ml: isDesktop ? `${NAV_RAIL_WIDTH}px` : 0,
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper'
      }}>
        <Box sx={{ flex: 1 }}>
          <Outlet />
        </Box>

      {/* Footer - offset by rail width on desktop.
          Uses CSS breakpoint (not useMediaQuery) so the margin is set by
          the stylesheet on first paint — avoids the CLS jump caused by JS
          resolving useMediaQuery after initial render. */}
      <Box
        component="footer"
        sx={{
          py: 1.5,
          px: 2,
          // No left margin here any more: the footer now sits INSIDE the
          // content column, which already carries the rail offset. Keeping it
          // would indent the footer by the rail width twice.
          bgcolor: 'background.paper',
        }}
      >
        <Container maxWidth="lg" sx={{ py: 0 }}>
          {/* Links and Copyright */}
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={0.75}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
              © 2026 IndexAlpha Inc. All rights reserved.
            </Typography>
            <Box display="flex" gap={2}>
              <Button
                size="small"
                onClick={() => navigate('/privacy')}
                sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.75rem', py: 0.25 }}
              >
                Privacy Policy
              </Button>
              <Button
                size="small"
                onClick={() => navigate('/terms')}
                sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.75rem', py: 0.25 }}
              >
                Terms & Conditions
              </Button>
              <Button
                size="small"
                onClick={() => navigate('/sitemap')}
                sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.75rem', py: 0.25 }}
              >
                Sitemap
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 0.75 }} />

          {/* Security Badge */}
          <Box display="flex" justifyContent="center" alignItems="center" gap={1.5} mb={0.5}>
            <Lock sx={{ fontSize: 16, color: 'grey.700' }} />
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }} fontWeight={600}>
              Built for Security
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
              • Our infrastructure and solutions are designed to meet SOC 2 Type II compliance standards
            </Typography>
          </Box>

          {/* Market Data Disclaimer */}
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', textAlign: 'center', mb: 0.5, fontSize: '0.65rem' }}>
            Market data updated daily. Not financial advice.
          </Typography>

          {/* Version Info — present for support, invisible in normal reading.
              Painted in `background.paper`, the footer's own colour, so it
              disappears against it while staying real text: select across the
              area, or open dev tools, and the build is right there. That is the
              point — "which build am I looking at?" is the first question of any
              incident, and an answer nobody can reach is not an answer.

              A theme TOKEN, never a literal, so it stays invisible in both
              light and dark. A hardcoded colour would show up the moment the
              theme changed, which is exactly the drift [[BUGS #173]] was.

              `aria-hidden` because a build number read aloud is noise to
              someone using a screen reader — it is developer metadata, not
              content. Hiding it from the accessibility tree does not affect
              text selection, so the developer path is untouched. */}
          <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
            <Typography
              variant="caption"
              aria-hidden="true"
              data-testid="build-version"
              sx={{ color: 'background.paper', fontSize: '0.65rem', userSelect: 'text' }}
            >
              {getVersionString()} • {getBuildDate()}
            </Typography>
          </Box>
        </Container>
      </Box>
      </Box>

      {/* Work you started that outlives the page you started it on
          ([[ROADMAP A.30]]). Mounted in the LAYOUT on purpose: a rebalance
          comparison takes minutes and both workflow pages tell the reader they
          can leave — which was only true if something outside those pages could
          still show the work. */}
      <ActiveWorkIndicator />

      {/* The sign-in dialog lives in ResponsiveLayout, so the phone shell has it too. */}

    </>
  );
};

export default Layout;